import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { assertIsAxiosError } from "_shared/_errors/errors";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { AxiosError, HttpStatusCode } from "axios";
import Session from "users/__session/session";
import FeeScheduleTemplateQuerySchema, {
  FeeScheduleTemplateQueryJSON,
} from "work/fee-schedule-templates/_api/queries/contracts/_schema/fee-schedule-template-query-schema";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import FeeScheduleTemplateQueryAPIService from "work/fee-schedule-templates/_api/queries/fee-schedule-template-query-api-service";
import FeeScheduleTemplateSchema, {
  FeeScheduleTemplateJSON,
} from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";

export type FeeScheduleTemplateLoadIssue = { id: Guid; issue: string };

export const isFeeScheduleTemplateLoadIssue = (value: object): value is FeeScheduleTemplateLoadIssue => {
  return "issue" in value;
};

export type FeeScheduleTemplateStoreState = {
  byId: {
    entries: Record<string, FeeScheduleTemplateJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, FeeScheduleTemplateQueryJSON>;
    lastSuccessfulResponses: Record<string, PaginatedResponse<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>>;
  };
};

const initialState: FeeScheduleTemplateStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
    lastSuccessfulResponses: {},
  },
};

export const populateFeeScheduleTemplatesByQuery = createAsyncThunk<
  {
    paginatedResponse: PaginatedResponse<FeeScheduleTemplateJSON | { id: Guid; issue: string }>;
    issueCount: number;
  },
  { session: Session; query: FeeScheduleTemplateQuery; interfaceName: string }
>(
  "feeScheduleTemplates/populateFeeScheduleTemplatesByQuery",
  async ({ session, query }: { session: Session; query: FeeScheduleTemplateQuery }, thunkAPI) => {
    const apiService = new FeeScheduleTemplateQueryAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const responseJson = await apiService.getFeeScheduleTemplatesByQuery(query, abortController);
      const validatedData: (FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue)[] = [];

      let issueCount = 0;
      responseJson.data.forEach((feeScheduleTemplate: FeeScheduleTemplateJSON) => {
        let id = Guid.EMPTY;
        try {
          id = Guid.fromJSON(feeScheduleTemplate.id) ?? id;
          if (FeeScheduleTemplateSchema.validateJSON(feeScheduleTemplate)) {
            validatedData.push(feeScheduleTemplate);
          }
        } catch (error) {
          validatedData.push({ id, issue: "Error loading feeScheduleTemplate." });
          console.error("Error validating feeScheduleTemplate JSON:", error);
          issueCount++;
        }
      });
      const paginatedResponse = PaginatedResponse.from<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>(
        validatedData,
        responseJson.headers
      );
      return { paginatedResponse, issueCount };
    } catch (error) {
      const apiError = error as AxiosError;
      return thunkAPI.rejectWithValue(
        apiError.response?.data ?? JSON.stringify(apiError) ?? "An unknown error occurred."
      );
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
  {
    condition: ({ query }: { session: Session; query: FeeScheduleTemplateQuery }, { getState }) => {
      const state = getState() as RootState;
      const queryKey = query.cacheKey;
      // Only dispatch if not already loading
      return !state.feeScheduleTemplates.byQuery.loading[queryKey];
    },
  }
);
export const populateFeeScheduleTemplatesById = createAsyncThunk<
  FeeScheduleTemplateJSON,
  { session: Session; id: Guid }
>(
  "feeScheduleTemplates/getFeeScheduleTemplatesById",
  async ({ session, id }: { session: Session; id: Guid }, thunkAPI) => {
    try {
      const apiService = new FeeScheduleTemplateQueryAPIService(session);
      const feeScheduleTemplate = await apiService.getFeeScheduleTemplateById(id);
      if (!feeScheduleTemplate) {
        return thunkAPI.rejectWithValue("Not found.");
      }
      return feeScheduleTemplate;
    } catch (error) {
      assertIsAxiosError(error);
      if (error.response?.status === HttpStatusCode.NotFound) {
        return thunkAPI.rejectWithValue("Not found.");
      }
      return thunkAPI.rejectWithValue(error.response?.data ?? JSON.stringify(error));
    }
  },
  {
    condition: ({ id }: { session: Session; id: Guid }, { getState }) => {
      const state = getState() as RootState;
      // Only dispatch if not already loading for this id
      return !state.feeScheduleTemplates.byId.loading[id.value];
    },
  }
);

const feeScheduleTemplatesSlice = createSlice({
  name: "feeScheduleTemplates",
  initialState,
  reducers: {
    addFeeScheduleTemplate: (state, action: PayloadAction<FeeScheduleTemplateJSON>) => {
      const id = action.payload?.id ? Guid.fromJSON(action.payload.id) : undefined;
      if (!id) return;

      const json = action.payload;
      if (json !== undefined && json !== null) {
        state.byId.entries[id.value] = json;
      }

      // Invalidate query cache
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeFeeScheduleTemplate: (state, action: PayloadAction<Guid>) => {
      const id = action.payload.value;
      delete state.byId.entries[id];

      // Invalidate query cache
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    updateFeeScheduleTemplateInterfaceQuery: (
      state,
      action: PayloadAction<{ interfaceName: string; query: FeeScheduleTemplateQuery }>
    ) => {
      const { interfaceName, query } = action.payload;

      const next = FeeScheduleTemplateQuerySchema.serialize(query);
      const prev = state.interfaceQueries.entries[interfaceName];

      if (prev && JSON.stringify(prev) === JSON.stringify(next)) return;

      state.interfaceQueries.entries[interfaceName] = next;
    },
    resetFeeScheduleTemplatesByQueryState: (state) => {
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateFeeScheduleTemplatesByQuery.pending, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = true;
      state.byQuery.error[query] = null;
    });
    builder.addCase(populateFeeScheduleTemplatesByQuery.fulfilled, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;

      const queryKey = action.meta.arg.query.cacheKey;
      const interfaceName = action.meta.arg.interfaceName;
      state.byQuery.loading[queryKey] = false;

      const { paginatedResponse, issueCount } = action.payload;
      state.byQuery.entries[queryKey] = paginatedResponse;
      if (interfaceName) {
        // Update last successful response for this interface
        state.interfaceQueries.lastSuccessfulResponses[interfaceName] = paginatedResponse;
      }
      if (issueCount > 0) {
        state.byQuery.error[queryKey] = `${issueCount} feeScheduleTemplate(s) could not be loaded due to errors.`;
      }
    });
    builder.addCase(populateFeeScheduleTemplatesByQuery.rejected, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = action.payload as string;
    });

    builder.addCase(populateFeeScheduleTemplatesById.pending, (state, action) => {
      if (!action.meta.arg.id) return;
      const id = action.meta.arg.id.value;
      state.byId.loading[id] = true;
      state.byId.error[id] = null;
    });
    builder.addCase(populateFeeScheduleTemplatesById.fulfilled, (state, action) => {
      if (!action.meta.arg.id) return;
      const id = action.meta.arg.id.value;

      state.byId.loading[id] = false;

      const feeScheduleTemplate = action.payload;
      const json = feeScheduleTemplate;
      if (json !== undefined && json !== null) {
        state.byId.entries[id] = json;
      }
    });
    builder.addCase(populateFeeScheduleTemplatesById.rejected, (state, action) => {
      if (!action.meta.arg.id) {
        return;
      }
      const id = action.meta.arg.id.value;
      state.byId.loading[id] = false;
      state.byId.error[id] = action.payload as string;
    });
  },
});

export const {
  addFeeScheduleTemplate,
  removeFeeScheduleTemplate,
  updateFeeScheduleTemplateInterfaceQuery,
  resetFeeScheduleTemplatesByQueryState,
} = feeScheduleTemplatesSlice.actions;

const selectFeeScheduleTemplatesEntriesForQuery = (state: RootState) => state.feeScheduleTemplates.byQuery.entries;
export const selectFeeScheduleTemplatesByQueryKey = createSelector(
  [selectFeeScheduleTemplatesEntriesForQuery, (_: RootState, queryKey: string) => queryKey],
  (
    entries: Record<string, PaginatedResponse<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>>,
    queryKey: string
  ): PaginatedResponse<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> | undefined => {
    const pagedJson = entries[queryKey];
    if (!pagedJson) return;

    const deserializedData: (FeeScheduleTemplate | FeeScheduleTemplateLoadIssue)[] = [];
    pagedJson.data.forEach((feeScheduleTemplate: FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue) => {
      try {
        if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplate)) {
          deserializedData.push(feeScheduleTemplate);
        } else {
          deserializedData.push(FeeScheduleTemplate.fromJSON(feeScheduleTemplate));
        }
      } catch (error) {
        console.error("Error deserializing feeScheduleTemplate:", error);
      }
    });

    return PaginatedResponse.from<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>(
      deserializedData,
      pagedJson.headers
    );
  }
);
export const getFeeScheduleTemplatesByQuery = (query?: FeeScheduleTemplateQuery) => (state: RootState) =>
  query ? selectFeeScheduleTemplatesByQueryKey(state, query.cacheKey) : undefined;

export const getIsLoadingFeeScheduleTemplatesByQuery = (query?: FeeScheduleTemplateQuery) => (state: RootState) => {
  if (query === undefined) return false;
  return state.feeScheduleTemplates.byQuery.loading[query.cacheKey] ?? false;
};
export const getErrorLoadingFeeScheduleTemplatesByQuery = (query?: FeeScheduleTemplateQuery) => (state: RootState) => {
  if (!query) return;
  return state.feeScheduleTemplates.byQuery.error[query.cacheKey];
};

export const getFeeScheduleTemplateById = (id: Guid) => {
  return (state: RootState) => {
    if (!id) return;
    const feeScheduleTemplateJson = state.feeScheduleTemplates.byId.entries[id.value];
    return FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
  };
};
export const getIsLoadingFeeScheduleTemplateById = (id: Guid) => (state: RootState) =>
  state.feeScheduleTemplates.byId.loading[id.value];
export const getErrorLoadingFeeScheduleTemplateById = (id: Guid) => (state: RootState) =>
  state.feeScheduleTemplates.byId.error[id.value];

export const getFeeScheduleTemplateQueryByInterface = (interfaceName: string) => (state: RootState) => {
  const json = state.feeScheduleTemplates.interfaceQueries.entries[interfaceName];
  if (!json) return;
  return FeeScheduleTemplateQuerySchema.deserialize(json);
};

const selectLastSuccessfulResponsesEntries = (state: RootState) =>
  state.feeScheduleTemplates.interfaceQueries.lastSuccessfulResponses;
const selectLastSuccessfulFeeScheduleTemplatesResponseByInterface = createSelector(
  [selectLastSuccessfulResponsesEntries, (_: RootState, interfaceName: string) => interfaceName],
  (
    entries: Record<string, PaginatedResponse<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>>,
    interfaceName: string
  ): PaginatedResponse<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue> | undefined => {
    const pagedJson = entries[interfaceName];
    if (!pagedJson) return;

    const feeScheduleTemplateData = pagedJson.data
      .map((feeScheduleTemplateJson: FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue | undefined) => {
        if (!feeScheduleTemplateJson) return undefined;
        if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson)) {
          return feeScheduleTemplateJson;
        }
        return FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
      })
      .filter((n): n is FeeScheduleTemplate | FeeScheduleTemplateLoadIssue => typeof n === "object" && n !== null);
    return PaginatedResponse.from<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>(
      feeScheduleTemplateData,
      pagedJson.headers
    );
  }
);
export const getLastSuccessfulFeeScheduleTemplateResponseByInterface =
  (interfaceName: string) => (state: RootState) => {
    return selectLastSuccessfulFeeScheduleTemplatesResponseByInterface(state, interfaceName);
  };

export default feeScheduleTemplatesSlice;
